import type {
	PutTimeSettingsInput,
	WorksiteScheduleDay,
	WorksiteTimeSettings,
} from "@/adapters/api";
import type {
	OvertimeRateField,
	ScheduleDayValues,
	TimeSettingsFormValues,
} from "../types";

export const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

export const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

export const WEEKDAY_LABELS: Record<number, string> = {
	0: "Domingo",
	1: "Segunda-feira",
	2: "Terça-feira",
	3: "Quarta-feira",
	4: "Quinta-feira",
	5: "Sexta-feira",
	6: "Sábado",
};

export const OVERTIME_RATE_FIELDS: {
	name: OvertimeRateField;
	label: string;
	apiField: keyof PutTimeSettingsInput;
}[] = [
	{
		name: "weekdayOvertimePct",
		label: "Dia útil",
		apiField: "weekday_overtime_pct",
	},
	{
		name: "saturdayOvertimePct",
		label: "Sábado",
		apiField: "saturday_overtime_pct",
	},
	{
		name: "sundayOvertimePct",
		label: "Domingo",
		apiField: "sunday_overtime_pct",
	},
	{
		name: "holidayOvertimePct",
		label: "Feriado",
		apiField: "holiday_overtime_pct",
	},
];

export function clockToMinutes(value: string): number | undefined {
	if (!TIME_PATTERN.test(value)) return undefined;
	const [hours, minutes] = value.split(":").map(Number);
	if (hours == null || minutes == null) return undefined;
	return hours * 60 + minutes;
}

export function parseBreakMinutes(value: string): number | undefined {
	const trimmed = value.trim();
	if (!/^\d+$/.test(trimmed)) return undefined;
	return Number(trimmed);
}

export function parsePercentage(value: string): number | undefined {
	const trimmed = value.trim().replace(",", ".");
	if (!/^\d+(\.\d+)?$/.test(trimmed)) return undefined;
	return Number(trimmed);
}

export function expectedMinutes(day: ScheduleDayValues): number | undefined {
	if (!day.enabled) return 0;
	const start = clockToMinutes(day.startsAt);
	const end = clockToMinutes(day.endsAt);
	const breakMinutes = parseBreakMinutes(day.breakMinutes);
	if (start == null || end == null || breakMinutes == null) return undefined;
	if (end <= start) return undefined;
	return Math.max(0, end - start - breakMinutes);
}

export function formatHours(total: number): string {
	const hours = Math.floor(total / 60);
	const minutes = total % 60;
	return `${hours}h ${String(minutes).padStart(2, "0")}m`;
}

export function weeklyExpectedMinutes(
	schedule: ScheduleDayValues[],
): number | undefined {
	let total = 0;
	for (const day of schedule) {
		const minutes = expectedMinutes(day);
		if (minutes == null) return undefined;
		total += minutes;
	}
	return total;
}

function toScheduleDayValues(day: WorksiteScheduleDay): ScheduleDayValues {
	const enabled = day.starts_at != null && day.ends_at != null;
	return {
		weekday: day.weekday,
		enabled,
		startsAt: day.starts_at ?? "",
		endsAt: day.ends_at ?? "",
		breakMinutes: String(day.break_minutes),
	};
}

export function toFormValues(
	settings: WorksiteTimeSettings,
): TimeSettingsFormValues {
	const byWeekday = new Map(settings.schedule.map((d) => [d.weekday, d]));
	return {
		weekdayOvertimePct: String(settings.weekday_overtime_pct),
		saturdayOvertimePct: String(settings.saturday_overtime_pct),
		sundayOvertimePct: String(settings.sunday_overtime_pct),
		holidayOvertimePct: String(settings.holiday_overtime_pct),
		schedule: WEEKDAY_ORDER.map((weekday) => {
			const day = byWeekday.get(weekday);
			return day
				? toScheduleDayValues(day)
				: {
						weekday,
						enabled: false,
						startsAt: "",
						endsAt: "",
						breakMinutes: "0",
					};
		}),
	};
}

export function toPutInput(
	values: TimeSettingsFormValues,
): PutTimeSettingsInput {
	return {
		weekday_overtime_pct: parsePercentage(values.weekdayOvertimePct) ?? 0,
		saturday_overtime_pct: parsePercentage(values.saturdayOvertimePct) ?? 0,
		sunday_overtime_pct: parsePercentage(values.sundayOvertimePct) ?? 0,
		holiday_overtime_pct: parsePercentage(values.holidayOvertimePct) ?? 0,
		schedule: values.schedule.map((day) => ({
			weekday: day.weekday,
			starts_at: day.enabled ? day.startsAt : null,
			ends_at: day.enabled ? day.endsAt : null,
			break_minutes: day.enabled
				? (parseBreakMinutes(day.breakMinutes) ?? 0)
				: 0,
		})),
	};
}
